import { normalizeWorkflowStatusKey } from '@/lib/work-items/operationalStatusConfig';
import { getWorkItemTypeRegistry } from '@/config/work-items/configLoader';

export type AppointmentAdminOverrideStatus =
  | 'scheduled'
  | 'on_route'
  | 'on_site'
  | 'in_progress'
  | 'paused'
  | 'resolved'
  | 'partial'
  | 'unresolved'
  | 'rescheduled'
  | 'cancelled'
  | 'closed'
  | 'admin_reopened';

const APPOINTMENT_ADMIN_OVERRIDE_STATUS_SET = new Set<AppointmentAdminOverrideStatus>([
  'scheduled',
  'on_route',
  'on_site',
  'in_progress',
  'paused',
  'resolved',
  'partial',
  'unresolved',
  'rescheduled',
  'cancelled',
  'closed',
  'admin_reopened',
]);

/** @deprecated Fallback only — SSOT allowed_from transitions are loaded from types.yaml via configLoader */
const APPOINTMENT_ADMIN_OVERRIDE_NEXT_STEPS: Record<string, AppointmentAdminOverrideStatus[]> = {
  open: ['scheduled'],
  new: ['scheduled'],
  idle: ['scheduled'],
  ready: ['on_route', 'in_progress'],
  scheduled: ['on_route', 'in_progress'],
  confirmed: ['on_route', 'in_progress'],
  on_hold: ['scheduled'],
  on_route: ['on_site', 'in_progress'],
  en_route: ['on_site', 'in_progress'],
  traveling: ['on_site', 'in_progress'],
  travel_paused: ['on_site', 'in_progress'],
  arrived: ['in_progress'],
  on_site: ['in_progress'],
  checked_in: ['paused', 'resolved', 'partial', 'rescheduled', 'unresolved', 'cancelled'],
  working: ['paused', 'resolved', 'partial', 'rescheduled', 'unresolved', 'cancelled'],
  in_progress: ['paused', 'resolved', 'partial', 'rescheduled', 'unresolved', 'cancelled'],
  over_schedule: ['paused', 'resolved', 'partial', 'rescheduled', 'unresolved', 'cancelled'],
  paused: ['in_progress', 'resolved', 'partial', 'rescheduled', 'unresolved', 'cancelled'],
  completing: ['resolved', 'partial', 'rescheduled', 'unresolved', 'cancelled'],
  resolved: ['closed'],
  partial: ['closed'],
  unresolved: ['closed'],
  rescheduled: ['scheduled'],
  cancelled: ['closed'],
  admin_reopened: ['in_progress', 'resolved', 'partial', 'unresolved', 'rescheduled', 'cancelled', 'closed'],
  closed: ['scheduled'],
  missed: ['scheduled'],
  no_show: ['scheduled'],
  archived: ['scheduled'],
};

export function normalizeAppointmentAdminOverrideStatusKey(value: unknown): AppointmentAdminOverrideStatus | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = normalizeWorkflowStatusKey(value);
  if (!normalized) {
    return null;
  }

  switch (normalized) {
    case 'en_route':
    case 'traveling':
    case 'travel_paused':
    case 'running_late':
      return 'on_route';
    case 'arrived':
      return 'on_site';
    case 'checked_in':
    case 'working':
    case 'over_schedule':
      return 'in_progress';
    case 'on_hold':
      return 'paused';
    case 'reschedule':
      return 'rescheduled';
    default:
      return APPOINTMENT_ADMIN_OVERRIDE_STATUS_SET.has(normalized as AppointmentAdminOverrideStatus)
        ? normalized as AppointmentAdminOverrideStatus
        : null;
  }
}

export function getAllowedAppointmentAdminOverrideStatuses(currentStatus: unknown): AppointmentAdminOverrideStatus[] {
  if (typeof currentStatus !== 'string') {
    return [];
  }

  const normalized = normalizeWorkflowStatusKey(currentStatus);
  const configLoader = getWorkItemTypeRegistry();
  const ssotAllowed = configLoader.getAllowedTransitions('appointment', normalized);
  if (ssotAllowed.length > 0) {
    return ssotAllowed
      .map(s => normalizeAppointmentAdminOverrideStatusKey(s))
      .filter((s): s is AppointmentAdminOverrideStatus => s !== null);
  }
  const allowed = APPOINTMENT_ADMIN_OVERRIDE_NEXT_STEPS[normalized];
  return Array.isArray(allowed) ? allowed : [];
}

export function isAllowedAppointmentAdminOverrideTransition(
  currentStatus: unknown,
  requestedStatus: unknown,
): boolean {
  const normalizedRequestedStatus = normalizeAppointmentAdminOverrideStatusKey(requestedStatus);
  if (!normalizedRequestedStatus) {
    return false;
  }

  const normalizedCurrentStatus = normalizeAppointmentAdminOverrideStatusKey(currentStatus);
  if (normalizedCurrentStatus === normalizedRequestedStatus) {
    return true;
  }

  return getAllowedAppointmentAdminOverrideStatuses(currentStatus).includes(normalizedRequestedStatus);
}

export function formatAppointmentAdminOverrideStatusLabel(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}